import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import '../../assets/styles/payment/publicPages.scss';
import '../../assets/styles/payment/orderPage.scss';
import usePageMeta from '../../services/page-meta';
import {resolveLocalePrefix, usePaymentPayload} from './helpers';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SUPPORT_CAPTIONS = [
    'Оплата картами Visa и Mastercard обеспечивается сервисом онлайн-платежей Portmone.com. Безопасность оплаты подтверждена международным аудитом PCI DSS. Служба поддержки: тел. ',
    'Розрахунок картками Visa та Mastercard забезпечується сервісом онлайн-платежів Portmone.com. Безпека оплати підтверджена міжнародним аудитом PCI DSS. Служба підтримки: тел. ',
    'Payment by Visa and Mastercard is provided by service of online-payments Portmone.com. Payment safety was confirmed by PCI DSS security audit. In case of questions call or email us: ',
];

function sanitizeName(value) {
    return String(value || '')
        .replace(/[\d]/g, '')
        .replace(/[$&+,:;=?@#|'<>.^*()%!-]/g, '');
}

function formatAccessDate(t) {
    const date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const monthKey = String(date.getMonth() + 1).padStart(2, '0');
    const monthName = t(`app.monthNames.${monthKey}`);
    const year = date.getFullYear();

    return `${day} ${monthName} ${year}`;
}

function resolveKindTitle(kind, t) {
    if ('material' === kind) {
        return t('payment.general.material');
    }

    if ('conference' === kind) {
        return t('payment.general.conference');
    }

    if ('testing' === kind) {
        return t('payment.general.testing');
    }

    return t('payment.general.course');
}

function resolveDescription(kind, t) {
    if ('material' === kind) {
        return t('payment.general.materialPaymentDescription');
    }

    if ('conference' === kind) {
        return t('payment.general.conferencePaymentDescription');
    }

    return t('payment.general.coursePaymentDescription');
}

export default function PaymentOrderPage({kind}) {
    const {t} = useTranslation();
    const {id, ticketId} = useParams();
    const history = useHistory();
    const location = useLocation();
    const currentId = 'conference' === kind ? ticketId : id;
    const endpoint = 'conference' === kind
        ? `/api/v1/payment/conferences/${currentId}`
        : `/api/v1/payment/${kind}/${currentId}`;
    const {payload, isLoading} = usePaymentPayload(endpoint);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState({});
    const [focusMap, setFocusMap] = useState({
        firstName: false,
        lastName: false,
        email: false,
    });
    const metaTitle = payload?.itemTitle || t('meta.paymentOrder.title', t('app.payment.payOrderTitle'));

    usePageMeta({
        title: metaTitle,
        description: t('meta.paymentOrder.description', t('app.description')),
        keywords: t('meta.paymentOrder.keywords', t('app.keywords')),
    });

    useEffect(() => {
        if (payload?.state !== 'active') {
            return;
        }

        setFirstName(payload?.user?.firstName || '');
        setLastName(payload?.user?.lastName || '');
        setEmail(payload?.user?.email || '');
    }, [payload]);

    const localePrefix = useMemo(
        () => resolveLocalePrefix(location.pathname),
        [location.pathname]
    );

    const handleGoBack = useCallback((event) => {
        event.preventDefault();

        if (window.history.length > 1) {
            history.goBack();
            return;
        }

        history.push(`${localePrefix}/`);
    }, [history, localePrefix]);

    const validate = useCallback(() => {
        const nextErrors = {};
        const normalizedFirstName = String(firstName || '').trim();
        const normalizedLastName = String(lastName || '').trim();
        const normalizedEmail = String(email || '').trim();

        if (!normalizedLastName) {
            nextErrors.lastName = t('payment.general.requiredField');
        }

        if (!normalizedFirstName) {
            nextErrors.firstName = t('payment.general.requiredField');
        }

        if (!normalizedEmail) {
            nextErrors.email = t('payment.general.requiredField');
        } else if (!EMAIL_PATTERN.test(normalizedEmail)) {
            nextErrors.email = t('payment.general.wrongEmail');
        }

        setErrors(nextErrors);
        return 0 === Object.keys(nextErrors).length;
    }, [email, firstName, lastName, t]);

    const handleSubmit = useCallback((event) => {
        if (!validate()) {
            event.preventDefault();
        }
    }, [validate]);

    if (isLoading || payload?.state !== 'active') {
        return <section className="payment-order-page payment-order-section" />;
    }

    const currentKind = payload?.kind || kind;
    const amount = payload?.amount || payload?.payment?.billAmount || '0.00';
    const payment = payload?.payment || {};
    const logos = payload?.assets || {};
    const links = payload?.links || {};
    const cashBackUrl = links.cashBackUrl || `${localePrefix}/payment/cashback`;
    const dataProtectionUrl = links.dataProtectionUrl || `${localePrefix}/payment/data-protection`;
    const accessDate = formatAccessDate(t);

    return (
        <div className="payment-order-page payment-support-page">
            <section className="payment-order-section">
                <div className="container">
                    <div className="payment-header flex-center">
                        <div className="link-block">
                            <button type="button" className="payment-back-btn js-go-back" onClick={handleGoBack}>
                                {t('app.back')}
                            </button>
                        </div>
                        <h1>{t('app.payment.payOrderTitle')}</h1>
                    </div>

                    <div className="wrap">
                        <form
                            action={payment.gatewayUrl || 'https://www.portmone.com.ua/gateway/'}
                            method="post"
                            id="payment-form"
                            onSubmit={handleSubmit}>
                            <div className="about-order-block-wrap">
                                <h4>{t('app.payment.info')}</h4>
                                <div className="order-info">
                                    <h6>{resolveKindTitle(currentKind, t)}</h6>
                                    <p>{payload?.itemTitle || ''}</p>
                                </div>
                                <div className="order-access-info flex-center wrap-flex">
                                    <div>
                                        <span className="title">{t('payment.general.dateAccess')}</span>
                                        {accessDate}
                                    </div>
                                    <div>
                                        <span className="title">{t('payment.general.accessSpan')}</span>
                                        {t('payment.general.unlimited')}
                                    </div>
                                </div>
                                <div className="order-user-info">
                                    <h4>{t('payment.general.yourData')}</h4>
                                    <div className="grid-row">
                                        <div className="js-form-group">
                                            <label htmlFor="lastName" className={`form-label${focusMap.lastName || lastName ? ' active' : ''}`}>
                                                {t('app.userInfo.lastName')}
                                            </label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                id="lastName"
                                                value={lastName}
                                                className={`form-input js-name-field${errors.lastName ? ' error-field' : ''}`}
                                                onFocus={() => setFocusMap((prev) => ({...prev, lastName: true}))}
                                                onBlur={() => setFocusMap((prev) => ({...prev, lastName: false}))}
                                                onChange={(event) => {
                                                    setLastName(sanitizeName(event.target.value));
                                                    setErrors((prev) => ({...prev, lastName: ''}));
                                                }}
                                            />
                                            <div className="error-block">
                                                <span className={`js-error${errors.lastName ? '' : ' hidden'}`}>{errors.lastName || ''}</span>
                                            </div>
                                        </div>
                                        <div className="js-form-group">
                                            <label htmlFor="firstName" className={`form-label${focusMap.firstName || firstName ? ' active' : ''}`}>
                                                {t('app.userInfo.firstName')}
                                            </label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                id="firstName"
                                                value={firstName}
                                                className={`form-input js-name-field${errors.firstName ? ' error-field' : ''}`}
                                                onFocus={() => setFocusMap((prev) => ({...prev, firstName: true}))}
                                                onBlur={() => setFocusMap((prev) => ({...prev, firstName: false}))}
                                                onChange={(event) => {
                                                    setFirstName(sanitizeName(event.target.value));
                                                    setErrors((prev) => ({...prev, firstName: ''}));
                                                }}
                                            />
                                            <div className="error-block">
                                                <span className={`js-error${errors.firstName ? '' : ' hidden'}`}>{errors.firstName || ''}</span>
                                            </div>
                                        </div>
                                        <div className="js-form-group">
                                            <label htmlFor="email" className={`form-label${focusMap.email || email ? ' active' : ''}`}>
                                                {t('app.userInfo.email')}
                                            </label>
                                            <input
                                                type="text"
                                                name="email"
                                                id="email"
                                                value={email}
                                                className={`form-input${errors.email ? ' error-field' : ''}`}
                                                onFocus={() => setFocusMap((prev) => ({...prev, email: true}))}
                                                onBlur={() => setFocusMap((prev) => ({...prev, email: false}))}
                                                onChange={(event) => {
                                                    setEmail(event.target.value);
                                                    setErrors((prev) => ({...prev, email: ''}));
                                                }}
                                            />
                                            <div className="error-block">
                                                <span className={`js-error${errors.email ? '' : ' hidden'}`}>{errors.email || ''}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="payment-block-wrap">
                                <div className="payment flex-center wrap-flex">
                                    <div className="payment-bill">
                                        <span>{t('payment.general.total')}</span>
                                        {amount} {t('app.currencies.uah')}
                                    </div>
                                    <div className="payment-submit flex-center">
                                        <input type="hidden" name="payee_id" value={payment.payeeId || ''} />
                                        <input type="hidden" name="shop_order_number" value={payment.shopOrderNumber || ''} />
                                        <input type="hidden" name="bill_amount" value={payment.billAmount || amount} />
                                        <input type="hidden" name="description" value={resolveDescription(currentKind, t)} />
                                        <input type="hidden" name="success_url" value={payment.successUrl || `${localePrefix}/`} />
                                        <input type="hidden" name="failure_url" value={payment.failureUrl || `${localePrefix}/`} />
                                        <input type="hidden" name="lang" value={payment.lang || 'uk'} />
                                        <input type="submit" value={t('payment.general.pay')} className="btn" />
                                        <div className="cards-block">
                                            <div className="flex-center cards-group-2">
                                                <img src={logos.visaLogoUrl || '/img/payment/visa.svg'} alt="Visa" />
                                                <img src={logos.mastercardLogoUrl || '/img/payment/mastercard.svg'} alt="MasterCard" />
                                            </div>
                                            <img src={logos.portmoneLogoUrl || '/img/payment/portmone.svg'} alt="Portmone" width="122" />
                                        </div>
                                    </div>
                                </div>

                                <div className="info-text">
                                    {SUPPORT_CAPTIONS.map((caption) => (
                                        <p key={caption}>
                                            {caption}
                                            <a href="tel:+380442000902">+380(44)2000902</a>,{' '}
                                            <a href="mailto:support@portmone.com">support@portmone.com</a>
                                        </p>
                                    ))}
                                    <div className="link-block">
                                        <Link to={cashBackUrl}>{t('payment.general.cashBack')}</Link>
                                        <Link to={dataProtectionUrl}>{t('payment.general.protection')}</Link>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
